import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OffboardingList } from "@shared/types/tenantOffboarding";
import type {
  CreateTenantInput,
  DeleteTenantBody,
  TenantDeleted,
  TenantNameAvailability,
  TenantView,
  UpdateTenantBody,
} from "@shared/types/tenants";
import { PROVISIONING_POLL_INTERVAL_MS } from "@/hooks/use-instances-admin";
import { apiRequest } from "@/lib/api";

/**
 * Tenants (FR-20): the top of the tenancy tree. A tenant owns instances; its `keyVaultName` is a
 * pointer to where its secrets live, never a secret (SEC-2). Round 6 T-04 (platform 0027): the
 * customer above each tenant folded into it, so this hook serves the one Tenants page.
 *
 * The list is fetched ONCE, with disabled rows included, and the screen filters it in memory. A
 * deployment has tens of tenants, not thousands, so a query key that changed with every
 * keystroke would buy nothing and cost a request per character, and it would make the status
 * filter a second network round trip to see rows the browser already holds.
 */
export const TENANTS_PATH = "/api/v2/platform/tenants";

/** Includes disabled rows: FR-20 disables, never deletes, so they must be reachable. */
const TENANTS_LIST_PATH = `${TENANTS_PATH}?includeDisabled=true`;

export const TENANTS_QUERY_KEY = [TENANTS_LIST_PATH] as const;

/**
 * `enabled` exists because the tenant list is not readable by everyone who can reach a screen
 * that wants it: the Instances screen shows the tenant filter to a PlatformAdmin and the grid to
 * a TenantAdmin, and firing this query for the second would answer 403 and raise an error the
 * screen would then have to explain away. Not asking is the honest form of not being allowed.
 */
export function useTenants(options: { enabled?: boolean; followSetup?: boolean } = {}) {
  return useQuery<TenantView[]>({
    queryKey: TENANTS_QUERY_KEY,
    enabled: options.enabled ?? true,
    // #921 review: the Tenants screen promises a new row turns Active when it is ready, so while
    // any row's own resources are still provisioning the list is read again; it stops once none is.
    refetchInterval: options.followSetup
      ? (query) => (anyTenantSettingUp(query.state.data) ? PROVISIONING_POLL_INTERVAL_MS : false)
      : false,
  });
}

/**
 * #921: why no instance can be made yet, one rule for Overview, Instances and the New instance
 * dialog. `none`: no tenant at all (create one); `disabled`: tenants exist but every one is
 * disabled (enable one on Tenants); null: an active tenant exists.
 */
export type TenantGap = "none" | "disabled" | null;

export function tenantGap(rows: readonly TenantView[]): TenantGap {
  if (rows.length === 0) return "none";
  return rows.some((row) => row.status === "active") ? null : "disabled";
}

/** Whether any row's own resources are still being set up (`resources.status` provisioning). */
export function anyTenantSettingUp(rows: readonly TenantView[] | undefined): boolean {
  return (rows ?? []).some((row) => row.resources?.status === "provisioning");
}

/** `GET /platform/tenants/name-available?name=` (round 5): the tenant dialog's live check. */
export function tenantNameAvailabilityPath(name: string): string {
  return `${TENANTS_PATH}/name-available?name=${encodeURIComponent(name)}`;
}

/**
 * Is this Name free for a new tenant? The caller debounces the typing and enables the query only
 * for a name the new-tenant rule allows, so a keystroke is not a request. Never cached as fresh:
 * another admin may take the name between two checks, and the create's 409 is the answer that
 * counts.
 */
export function useTenantNameAvailability(name: string, enabled: boolean) {
  return useQuery<TenantNameAvailability>({
    queryKey: [tenantNameAvailabilityPath(name)],
    enabled,
    staleTime: 0,
    retry: false,
  });
}

/** Reads again every list a tenant write can change: tenants, instances and, on an edit, `/me`. */
function invalidateAfterWrite(
  queryClient: ReturnType<typeof useQueryClient>,
  includeMe: boolean,
): Promise<unknown> {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const path = String(query.queryKey[0]);
      return (
        path.startsWith(TENANTS_PATH) ||
        path.startsWith("/api/v2/platform/instances") ||
        (includeMe && path === "/api/v2/me")
      );
    },
  });
}

/**
 * A create starts the tenant's first instances too (round 5), so the instance lists (the
 * Instances screen, its filter and its create dialog) are read again with the tenant list.
 */
export function useCreateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTenantInput) =>
      apiRequest<TenantView>(TENANTS_PATH, { method: "POST", body }),
    // B-08: the new tenant's instances belong in the picker at once.
    onSuccess: () => invalidateAfterWrite(queryClient, true),
  });
}

/**
 * A rename shows in the switcher and a disabled tenant's instances leave the picker, so the
 * tenant and instance lists and `/me` are read again.
 */
export function useUpdateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTenantBody }) =>
      apiRequest<TenantView>(`${TENANTS_PATH}/${id}`, {
        method: "PUT",
        body,
        // #1110: a status change (disable, enable) needs a recent sign-in. The server asks only then.
        stepUp: "change",
      }),
    onSuccess: () => invalidateAfterWrite(queryClient, true),
  });
}

/**
 * F32 PR3: the deleted tenants, which every other list leaves out (the Tenants page's "Deleted"
 * filter). Asked only while that filter is chosen.
 */
export const DELETED_TENANTS_PATH = `${TENANTS_PATH}?deleted=true`;

export function useDeletedTenants(enabled: boolean) {
  return useQuery<TenantView[]>({ queryKey: [DELETED_TENANTS_PATH], enabled });
}

/** A deleted tenant's offboarding list (read-only); null asks nothing. */
export function tenantOffboardingPath(id: string): string {
  return `${TENANTS_PATH}/${id}/offboarding`;
}

export function useTenantOffboarding(id: string | null) {
  return useQuery<OffboardingList>({
    queryKey: [id ? tenantOffboardingPath(id) : "offboarding:none"],
    enabled: id !== null,
  });
}

/**
 * F32 PR3: deletes a DISABLED tenant (the Name typed, a reason). It leaves every list but the
 * Deleted one and `/me`, so all three are read again.
 */
export function useDeleteTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DeleteTenantBody }) =>
      apiRequest<TenantDeleted>(`${TENANTS_PATH}/${id}/delete`, { method: "POST", body }),
    onSuccess: () => invalidateAfterWrite(queryClient, true),
  });
}
